import React, { useState, useEffect, useCallback } from 'react';
import MobileWeekSelector from './MobileWeekSelector';
import MobileDaySelector from './MobileDaySelector';
import MobileSlotAdder from './MobileSlotAdder';
import MobileSlotList from './MobileSlotList';
import timeSlotService from '../../services/timeSlotService';

/**
 * MobileAvailabilityView - Mobile-optimized availability management interface
 *
 * Provides a touch-friendly interface for managing time slots on mobile devices
 */
const MobileAvailabilityView = () => {
  // Get Monday of current week
  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // State
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getMondayOfWeek(new Date()));
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Format date to YYYY-MM-DD
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Group slots by date
  const getSlotsMap = useCallback(() => {
    const map = {};
    slots.forEach(slot => {
      // Handle both formats: slot.start (Date object) or slot.date (YYYY-MM-DD or ISO string)
      let dateKey;
      if (slot.start) {
        // Desktop format: has start/end Date objects
        const slotDate = new Date(slot.start);
        dateKey = formatDateKey(slotDate);
      } else if (slot.date) {
        // Backend format: has date field (could be Date object or ISO string)
        const slotDate = new Date(slot.date);
        dateKey = formatDateKey(slotDate);
      } else {
        console.warn('📱 Slot missing date info:', slot);
        return;
      }

      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(slot);
    });
    console.log('📱 Slots grouped by date:', map);
    console.log('📱 Total slots:', slots.length, 'Grouped into', Object.keys(map).length, 'days');
    return map;
  }, [slots]);

  // Get slots for selected date
  const getSelectedDateSlots = () => {
    const dateKey = formatDateKey(selectedDate);
    const slotsMap = getSlotsMap();
    return slotsMap[dateKey] || [];
  };

  // Fetch slots for current week
  const fetchSlots = useCallback(async () => {
    try {
      setLoading(true);

      // Calculate week range
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const params = {
        startDate: formatDateKey(currentWeekStart),
        endDate: formatDateKey(weekEnd),
        status: 'available'
      };

      const response = await timeSlotService.getMyTimeSlots(params);
      console.log('📱 Mobile: Fetched slots response:', response);

      // Handle different response formats
      const fetchedSlots = response.data?.timeSlots || response.slots || response.data?.slots || [];
      console.log('📱 Mobile: Extracted slots:', fetchedSlots);

      setSlots(fetchedSlots);
    } catch (error) {
      console.error('Error fetching slots:', error);
      showToast('Failed to load slots', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart]);

  // Load slots when week changes
  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Handle week change
  const handleWeekChange = (direction) => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newWeekStart);

    // Update selected date to same day of week in new week
    const dayOfWeek = selectedDate.getDay();
    const newSelectedDate = new Date(newWeekStart);
    newSelectedDate.setDate(newSelectedDate.getDate() + (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    setSelectedDate(newSelectedDate);
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  // Handle add slot
  const handleAddSlot = async (startDate, endDate, errorMessage) => {
    // If error message is provided, it's a validation error
    if (errorMessage) {
      showToast(errorMessage, 'error');
      return;
    }

    try {
      setLoading(true);

      // Calculate duration in minutes
      const durationMs = endDate - startDate;
      const durationMinutes = Math.floor(durationMs / (1000 * 60));

      const slotData = {
        date: formatDateKey(startDate),
        startTime: `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`,
        endTime: `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`,
        duration: durationMinutes,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      await timeSlotService.createTimeSlot(slotData);

      // Refresh slots
      await fetchSlots();

      showToast('Time slot added successfully!', 'success');
    } catch (error) {
      console.error('Error adding slot:', error);
      showToast(error.response?.data?.message || 'Failed to add slot', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete slot
  const handleDeleteSlot = async (slotId) => {
    try {
      setLoading(true);

      await timeSlotService.deleteTimeSlot(slotId);

      // Refresh slots
      await fetchSlots();

      showToast('Time slot removed', 'success');
    } catch (error) {
      console.error('Error deleting slot:', error);
      showToast('Failed to delete slot', 'error');
    } finally {
      setLoading(false);
    }
  };

  const slotsMap = getSlotsMap();
  const selectedDateSlots = getSelectedDateSlots();

  return (
    <div className="w-full max-w-2xl mx-auto p-4 space-y-4">
      {/* Toast notification */}
      {toast.show && (
        <div
          className={`
            fixed top-4 left-1/2 -translate-x-1/2 z-50
            px-6 py-3 rounded-lg shadow-lg
            animate-slide-down
            ${toast.type === 'success'
              ? 'bg-green-500 text-white'
              : 'bg-red-500 text-white'
            }
          `}
        >
          {toast.message}
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </div>
      )}

      {/* Week Selector */}
      <MobileWeekSelector
        currentWeekStart={currentWeekStart}
        onWeekChange={handleWeekChange}
      />

      {/* Day Selector */}
      <MobileDaySelector
        currentWeekStart={currentWeekStart}
        selectedDate={selectedDate}
        slotsMap={slotsMap}
        onDateSelect={handleDateSelect}
      />

      {/* Slot Adder */}
      <MobileSlotAdder
        selectedDate={selectedDate}
        onAddSlot={handleAddSlot}
        existingSlots={selectedDateSlots}
      />

      {/* Slot List */}
      <MobileSlotList
        slots={selectedDateSlots}
        selectedDate={selectedDate}
        onDeleteSlot={handleDeleteSlot}
      />
    </div>
  );
};

export default MobileAvailabilityView;
